import React from 'react';
import { Link } from 'react-router-dom';
import StudyStayLogo from '../components/StudyStayLogo';
import WaitlistForm from '../components/WaitlistForm';
import ScrollProgress from '../components/ScrollProgress';
import Reveal from '../components/Reveal';
import ParallaxBackground from '../components/ParallaxBackground';
import {
  Search,
  Calendar,
  MessageCircle,
  FileText,
  Shield,
  Home,
  Building2,
  CheckCircle,
  ArrowRight,
  Settings,
  Instagram,
} from 'lucide-react';

const journeySteps = [
  { icon: Search, label: 'Search', color: 'text-primary-500' },
  { icon: Calendar, label: 'Visit', color: 'text-primary-600' },
  { icon: MessageCircle, label: 'Chat', color: 'text-primary-500' },
  { icon: FileText, label: 'Contract', color: 'text-primary-600' },
  { icon: Settings, label: 'Manage', color: 'text-primary-500' },
  { icon: Shield, label: 'Secure renting', color: 'text-primary-600' },
];

const benefits = [
  {
    icon: CheckCircle,
    title: 'End-to-End Platform',
    description: 'Everything you need in one place, from property search to signed contracts.',
  },
  {
    icon: Shield,
    title: 'Verified & Secure',
    description: 'All properties are verified and all transactions are secure and legally compliant.',
  },
  {
    icon: MessageCircle,
    title: 'Direct Communication',
    description: 'Chat directly with landlords and tenants for quick responses and clear communication.',
  },
];

export default function ComingSoon() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 relative overflow-hidden">
      <ScrollProgress />
      <ParallaxBackground />

      {/* Header */}
      <header className="relative z-10 pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StudyStayLogo className="w-12 h-12" />
                <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600">
                  StudyStay
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/coming-soon/landlord"
                  className="px-4 py-2 rounded-xl bg-white text-gray-700 font-semibold shadow-sm hover:shadow-md border border-gray-200 transition-all duration-200"
                >
                  Landlord
                </Link>
                <Link
                  to="/coming-soon/partnerships"
                  className="px-4 py-2 rounded-xl bg-white text-gray-700 font-semibold shadow-sm hover:shadow-md border border-gray-200 transition-all duration-200"
                >
                  Partnerships
                </Link>
                <a
                  href="https://www.instagram.com/studystay_app?igsh=eWZjcDYwbW9yZmYw&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="w-5 h-5 text-primary-600" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-12 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal delay={100}>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              The all-in-one student housing platform.
            </h2>
          </Reveal>

          <Reveal delay={200}>
            <p className="text-xl md:text-2xl text-gray-700 mb-4 font-medium">
              From search to signature — StudyStay manages the entire rental journey.
            </p>
          </Reveal>

          {/* Journey Flow */}
          <Reveal delay={300}>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 mb-12 py-8">
              {journeySteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.label}>
                    <div className="flex flex-col items-center gap-2 group">
                      <div className="w-14 h-14 md:w-16 md:h-16 bg-white rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                        <Icon className={`w-7 h-7 md:w-8 md:h-8 ${step.color}`} />
                      </div>
                      <span className="text-sm md:text-base font-medium text-gray-700">
                        {step.label}
                      </span>
                    </div>
                    {index < journeySteps.length - 1 && (
                      <ArrowRight className="w-5 h-5 md:w-6 md:h-6 text-gray-400 hidden md:block" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </Reveal>

          {/* Waitlist Form */}
          <Reveal delay={400}>
            <div className="max-w-md mx-auto mb-16">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Be the first to access StudyStay.
              </h3>
              <p className="text-gray-600 mb-8">
                Join the waitlist and get early access when we launch.
              </p>
              <WaitlistForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* For Students / For Landlords */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Home className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Students</h3>
                <p className="text-gray-600 leading-relaxed">
                  Find your perfect student accommodation quickly and easily. Browse verified listings,
                  schedule viewings, chat with landlords, and sign contracts digitally — all in one
                  platform.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">For Landlords</h3>
                <p className="text-gray-600 leading-relaxed">
                  Manage your properties and connect with students effortlessly. Streamline your rental
                  process with our professional tools. Handle everything from listing to signed contracts.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Why StudyStay?
            </h3>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Reveal key={benefit.title} delay={index * 100}>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-primary-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-gray-200 py-8 px-4 sm:px-6 lg:px-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-2 mb-4">
              <StudyStayLogo className="w-6 h-6" />
              <span className="text-gray-700 font-semibold">StudyStay</span>
            </div>
            <p className="text-gray-600 font-medium mb-4">Coming Soon — Web & iOS</p>
            <div className="flex items-center justify-center gap-4 mb-4">
              <a
                href="https://www.instagram.com/studystay_app?igsh=eWZjcDYwbW9yZmYw&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 hover:scale-110"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5 text-primary-600" />
              </a>
            </div>
            <div className="flex items-center justify-center gap-6 mb-4 flex-wrap">
              <a
                href="/privacy"
                className="text-sm text-gray-600 hover:text-primary-600 transition-colors duration-200"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="text-sm text-gray-600 hover:text-primary-600 transition-colors duration-200"
              >
                Terms of Service
              </a>
            </div>
            <p className="text-sm text-gray-500">© 2026 Nolf Technologies OÜ. All rights reserved.</p>
          </Reveal>
        </div>
      </footer>
    </div>
  );
}
